import { useCallback } from 'react';
import {
  useTabsStore,
  setTabStripOrder,
  setRequestTabs,
  setCollectionTabs,
  setSchemaTabs,
  setDbManagerTabs,
  setActiveRequestTabId,
  setActiveCollectionTabId,
  setActiveSchemaTabId,
  setActiveDbManagerTabId,
  setActiveTabType,
  setTabsHydrating,
} from '../../store/tabsStore';
import { useCollectionsStore, setSelectedCollection } from '../../store/collectionsStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { isValidWorkspaceTabSnapshot, normalizeSnapshot } from '../../utils/workspaceTabPersistence';
import { saveWorkspaceTabs, loadWorkspaceTabs } from '../../services/workspaceTabStore';
import { isDbConnectionLive } from '../../extensions/capabilities/db';
import { pruneDbQueryResultCache } from '../../store/dbQueryResultCache';

/** Persisting, reordering and restoring a workspace's tabs. */
export function useWorkspaceTabRestore({
  persistTabsTimeoutRef,
  workspaceTabStateRef,
  initializedRef,
  tabsHydratingRef,
  closeAllTabs,
}) {
  const flushWorkspaceTabPersist = useCallback(() => {
    if (persistTabsTimeoutRef.current) {
      clearTimeout(persistTabsTimeoutRef.current);
      persistTabsTimeoutRef.current = null;
    }
    const workspaceId = useWorkspaceStore.getState().currentWorkspace?.id;
    if (!workspaceId || !initializedRef.current) return;
    saveWorkspaceTabs(workspaceId, workspaceTabStateRef.current).catch(() => {});
  }, [persistTabsTimeoutRef, initializedRef, workspaceTabStateRef]);

  const scheduleWorkspaceTabPersist = useCallback(() => {
    if (persistTabsTimeoutRef.current) {
      clearTimeout(persistTabsTimeoutRef.current);
    }
    persistTabsTimeoutRef.current = setTimeout(flushWorkspaceTabPersist, 400);
  }, [flushWorkspaceTabPersist, persistTabsTimeoutRef]);

  const handleTabsReorder = useCallback((newStripOrder) => {
    // Pinned tabs (currently: the Task Manager "Boards" tab) must always
    // stay first, regardless of what the raw drag produced — a stable sort
    // hoists them to the front without disturbing relative order otherwise.
    const { taskManagerTabs: boardTabs, projectsTabs: projectTabs } = useTabsStore.getState();
    const pinnedIds = new Set([...boardTabs, ...projectTabs].filter((t) => t.pinned).map((t) => t.id));
    const orderedStripOrder = pinnedIds.size
      ? [...newStripOrder].sort((a, b) => (pinnedIds.has(b.id) ? 1 : 0) - (pinnedIds.has(a.id) ? 1 : 0))
      : newStripOrder;
    // EnhancedTabManager only knows about (and reorders) the isolated strip
    // it was handed — merge back in, don't overwrite, whichever context's
    // entries aren't part of this reorder (the other isolated strip).
    setTabStripOrder((prev) => {
      const untouched = prev.filter((e) => !orderedStripOrder.some((n) => n.id === e.id && n.type === e.type));
      return [...untouched, ...orderedStripOrder];
    });
    scheduleWorkspaceTabPersist();
  }, [scheduleWorkspaceTabPersist]);

  const applyWorkspaceTabSnapshot = useCallback(
    (snapshot) => {
      if (!isValidWorkspaceTabSnapshot(snapshot)) {
        closeAllTabs({ clearPersistedResponses: false });
        return;
      }

      const normalized = normalizeSnapshot(snapshot);

      setRequestTabs(normalized.requestTabs);
      setCollectionTabs(normalized.collectionTabs);
      setSchemaTabs(normalized.schemaTabs || []);
      // Task Manager tabs (the pinned "Boards" tab and any open boards) aren't
      // part of the per-workspace snapshot — they live for the session and must
      // survive a workspace switch. The snapshot's tabStripOrder drops their
      // strip entries (task-manager-board isn't a TAB_STRIP_TYPE), so preserve
      // the existing ones instead of overwriting them away.
      setTabStripOrder((prev) => {
        // Task Manager and Documents tabs are session-lived (not part of the
        // per-workspace snapshot); preserve their strip entries.
        const preservedExtension = prev.filter((e) => e.type === 'task-manager-board' || e.type === 'projects-project' || e.type === 'document-editor');
        return [...preservedExtension, ...normalized.tabStripOrder];
      });
      setActiveRequestTabId(normalized.activeRequestTabId);
      setActiveCollectionTabId(normalized.activeCollectionTabId);
      setActiveSchemaTabId(normalized.activeSchemaTabId ?? null);
      // DB Manager tabs are restored, but their connId references a connection
      // that (on a fresh launch) died with the previous process — flag those so
      // DbManagerResultsTab shows its reconnect screen instead of querying a
      // dead connection. On a workspace switch the connection may still be
      // live (they aren't workspace-scoped); those skip the flag entirely.
      // Cached query results from the outgoing workspace's tabs must not
      // outlive the switch (large result sets held in memory otherwise).
      pruneDbQueryResultCache((normalized.dbManagerTabs || []).map((t) => t.id));
      setDbManagerTabs(
        (normalized.dbManagerTabs || []).map((tab) => {
          // The restored draft becomes the unsaved-dot baseline — the true
          // on-disk script content isn't read at hydration time, so a draft
          // saved-with-changes at exit restores as "clean" on purpose.
          const base = { ...tab, savedSql: tab.sql || '' };
          return isDbConnectionLive(tab.extensionId, tab.connId) ? base : { ...base, needsReconnect: true };
        })
      );
      setActiveDbManagerTabId(normalized.activeDbManagerTabId ?? null);
      setActiveTabType(
        normalized.activeTabType === 'db-manager-table' ||
          normalized.activeTabType === 'task-manager-board' ||
          normalized.activeTabType === 'projects-project' ||
          normalized.activeTabType === 'document-editor'
          ? 'request'
          : normalized.activeTabType
      );

      if (normalized.activeTabType === 'collection' && normalized.activeCollectionTabId) {
        const collections = useCollectionsStore.getState().collections;
        const tab = normalized.collectionTabs.find((t) => t.id === normalized.activeCollectionTabId);
        const col = tab
          ? collections.find((c) => String(c.id) === String(tab.collectionId))
          : null;
        setSelectedCollection(col || null);
      } else {
        setSelectedCollection(null);
      }
    },
    [closeAllTabs]
  );

  const restoreWorkspaceTabs = useCallback(
    async (workspaceId) => {
      tabsHydratingRef.current = true;
      setTabsHydrating(true);
      try {
        if (!workspaceId) {
          closeAllTabs({ clearPersistedResponses: false });
          return;
        }
        const snapshot = await loadWorkspaceTabs(workspaceId);
        applyWorkspaceTabSnapshot(snapshot);
      } finally {
        tabsHydratingRef.current = false;
        setTabsHydrating(false);
        initializedRef.current = true;
      }
    },
    [applyWorkspaceTabSnapshot, closeAllTabs, tabsHydratingRef, initializedRef]
  );

  return {
    flushWorkspaceTabPersist,
    scheduleWorkspaceTabPersist,
    handleTabsReorder,
    applyWorkspaceTabSnapshot,
    restoreWorkspaceTabs,
  };
}
